import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Shield, ChartPie, TrendingUpDown, ChartNoAxesGantt, Users, House, Building2, Plane, NotebookPen, ShieldUser, Check, X as XIcon, Mail, Menu, Search, FileText, Award } from 'lucide-react';
import { FaXTwitter, FaInstagram, FaFacebook, FaYoutube, FaReddit } from 'react-icons/fa6';
import AuthModal from '../Auth/AuthModal';
import InfoModal from '../Modals/InfoModal';
import './LandingPage.css';
import { locationTypes, locationNames, getStateAbbreviation } from '../../constants/locations';
import { getCrimeDisplayName } from '../../constants/crimes';
import { parseAppQuery } from '../../utils/urlRouting';
import { useResetScrollAfterMobileKeyboard } from '../../hooks/useResetScrollAfterMobileKeyboard';

const formatDeepLinkName = (locationInfo) => {
  if (!locationInfo?.name) return '';
  const { name, type } = locationInfo;
  if ((type === 'city' || type === 'county') && name.includes(', ')) {
    const parts = name.split(', ');
    const place = parts.slice(0, -1).join(', ');
    const stateAbbr = getStateAbbreviation(parts[parts.length - 1].trim());
    return `${place}, ${stateAbbr}`;
  }
  return name;
};

const LandingPage = ({ onSearchLocationSelected, deepLinkLocation, deepLinkSearch }) => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authView, setAuthView] = useState('log_in');
  const [showInfoModal, setShowInfoModal] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState(null);
  const [heroSearchQuery, setHeroSearchQuery] = useState('');
  const [heroSearchResults, setHeroSearchResults] = useState([]);
  const [showHeroDropdown, setShowHeroDropdown] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [pendingSearchLocation, setPendingSearchLocation] = useState(null);
  const [heroHighlightedIndex, setHeroHighlightedIndex] = useState(-1);
  const [heroIsNavigatingWithKeyboard, setHeroIsNavigatingWithKeyboard] = useState(false);
  const [heroDropdownMaxHeight, setHeroDropdownMaxHeight] = useState(480);

  const heroSearchRef = useRef(null);
  const heroInputRef = useRef(null);
  const heroDropdownRef = useRef(null);
  const loginJustSucceeded = useRef(false);
  const signUpJustSucceeded = useRef(false);

  useResetScrollAfterMobileKeyboard();

  useLayoutEffect(() => {
    const lock = showAuthModal || showInfoModal;
    if (!lock) {
      document.body.classList.remove('landing-modal-open');
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      return;
    }
    document.body.classList.add('landing-modal-open');
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    return () => {
      document.body.classList.remove('landing-modal-open');
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [showAuthModal, showInfoModal]);

  const openAuth = (view) => {
    setAuthView(view);
    setShowAuthModal(true);
  };

  const scrollToSection = (sectionId) => {
    setShowMobileMenu(false);
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 75;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterStatus('success');
    setNewsletterEmail('');
    
    setTimeout(() => {
      setNewsletterStatus(null);
    }, 5000);
  };

  const getFlattenedHeroResults = useCallback(() => {
    const results = [];
    [...locationTypes].reverse().forEach(locationType => {
      results.push(...heroSearchResults.filter(r => r.type === locationType.value));
    });
    return results;
  }, [heroSearchResults]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (heroSearchRef.current && !heroSearchRef.current.contains(event.target)) {
        setShowHeroDropdown(false);
        setHeroHighlightedIndex(-1);
        setHeroIsNavigatingWithKeyboard(false);
        if (!pendingSearchLocation) {
          setHeroSearchQuery('');
          setHeroSearchResults([]);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [pendingSearchLocation]);

  useEffect(() => {
    if (showHeroDropdown && heroInputRef.current) {
      const inputRect = heroInputRef.current.getBoundingClientRect();
      const dropdownTop = inputRect.bottom + 8;
      const available = window.innerHeight * 0.95 - dropdownTop;
      setHeroDropdownMaxHeight(Math.max(120, available));
    }
  }, [showHeroDropdown, heroSearchResults]);

  const heroScrollToHighlighted = (index) => {
    if (heroDropdownRef.current) {
      const element = heroDropdownRef.current.querySelector(`[data-index="${index}"]`);
      if (element) element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  };

  const handleHeroKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setHeroSearchQuery('');
      setHeroSearchResults([]);
      setShowHeroDropdown(false);
      setHeroHighlightedIndex(-1);
      setHeroIsNavigatingWithKeyboard(false);
      setPendingSearchLocation(null);
      onSearchLocationSelected?.(null);
      heroInputRef.current?.focus();
      return;
    }

    if (!showHeroDropdown || heroSearchResults.length === 0) return;

    const flatResults = getFlattenedHeroResults();
    const maxIndex = flatResults.length - 1;

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        e.preventDefault();
        setHeroIsNavigatingWithKeyboard(true);
        setHeroHighlightedIndex(prev => {
          const nextIndex = e.key === 'ArrowDown'
            ? (prev < maxIndex ? prev + 1 : 0)
            : (prev > 0 ? prev - 1 : maxIndex);
          heroScrollToHighlighted(nextIndex);
          return nextIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (flatResults.length > 0) {
          handleHeroLocationSelect(heroHighlightedIndex === -1 ? flatResults[0] : flatResults[heroHighlightedIndex]);
          heroInputRef.current?.blur();
        }
        break;
      default:
        setHeroIsNavigatingWithKeyboard(false);
        break;
    }
  };

  const normalizeName = (name) => {
    return name.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/-/g, ' ')
      .replace(/[.(),\-']/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const getDisplayName = (result) => {
    if ((result.type === 'county' || result.type === 'city') && result.name.includes(', ')) {
      const parts = result.name.split(', ');
      const local = parts.slice(0, -1).join(', ');
      const state = parts[parts.length - 1];
      return `${local}, ${getStateAbbreviation(state.trim())}`;
    }
    return result.name;
  };

  const processLocationResults = (locations, type, query) => {
    const normalizedQuery = normalizeName(query);
    const trimmedQuery = query.trim();

    if (normalizedQuery.length <= 2) {
      return locations
        .filter(name => normalizeName(name).includes(normalizedQuery))
        .map(name => ({ name, type, isExactMatch: normalizeName(name).startsWith(normalizedQuery) }))
        .sort((a, b) => (a.isExactMatch && !b.isExactMatch ? -1 : !a.isExactMatch && b.isExactMatch ? 1 : 0))
        .slice(0, 5);
    }

    if ((type === 'county' || type === 'city') && normalizedQuery.length > 0) {
      const startsWithMatches = [];
      const containsMatches = [];
      const hasSpace = trimmedQuery.includes(' ');

      locations.forEach(fullName => {
        if (fullName.includes(', ')) {
          const [localName, stateName] = fullName.split(', ');
          const normalizedLocalName = normalizeName(localName);
          const normalizedStateName = normalizeName(stateName);
          const normalizedStateAbbr = normalizeName(getStateAbbreviation(stateName));

          let matches = false;
          let startsWithCity = false;
          let exactCityMatch = false;

          if (hasSpace) {
            const lastSpaceIndex = trimmedQuery.lastIndexOf(' ');
            const cityPart = trimmedQuery.substring(0, lastSpaceIndex).trim();
            const statePart = trimmedQuery.substring(lastSpaceIndex + 1).trim();
            const normalizedCityPart = normalizeName(cityPart);
            const normalizedStatePart = normalizeName(statePart);
            const exactMatch = normalizedLocalName.includes(normalizeName(trimmedQuery));
            const cityAndStateMatch = statePart &&
              normalizedLocalName.includes(normalizedCityPart) &&
              (normalizedStateName.startsWith(normalizedStatePart) || normalizedStateAbbr.startsWith(normalizedStatePart));
            if (exactMatch || cityAndStateMatch) {
              matches = true;
              startsWithCity = normalizedLocalName.startsWith(normalizeName(trimmedQuery)) ||
                normalizedLocalName.startsWith(normalizedCityPart);
              exactCityMatch = normalizedLocalName === normalizeName(trimmedQuery) ||
                normalizedLocalName === normalizedCityPart;
            }
          } else {
            if (normalizedLocalName.includes(normalizedQuery) ||
                normalizedStateName.includes(normalizedQuery) ||
                normalizedStateAbbr.includes(normalizedQuery)) {
              matches = true;
              startsWithCity = normalizedLocalName.startsWith(normalizedQuery);
              exactCityMatch = normalizedLocalName.startsWith(normalizedQuery);
            }
          }

          if (matches) {
            const matchData = { name: fullName, localName, normalizedLocalName, type, isExactMatch: exactCityMatch, matchType: startsWithCity ? 'starts_with' : 'contains' };
            if (startsWithCity) startsWithMatches.push(matchData);
            else containsMatches.push(matchData);
          }
        }
      });

      return [...startsWithMatches, ...containsMatches]
        .sort((a, b) => {
          if (a.isExactMatch && !b.isExactMatch) return -1;
          if (!a.isExactMatch && b.isExactMatch) return 1;
          if (a.matchType === 'starts_with' && b.matchType === 'contains') return -1;
          if (a.matchType === 'contains' && b.matchType === 'starts_with') return 1;
          return a.localName.localeCompare(b.localName);
        })
        .slice(0, 5);
    }

    const startMatches = locations
      .filter(name => normalizeName(name).startsWith(normalizedQuery))
      .map(name => ({ name, type, isExactMatch: true, matchType: 'starts_with' }));
    const containsMatches = locations
      .filter(name => { const n = normalizeName(name); return n.includes(normalizedQuery) && !n.startsWith(normalizedQuery); })
      .map(name => ({ name, type, isExactMatch: false, matchType: 'contains' }));

    return [...startMatches, ...containsMatches].slice(0, 5);
  };

  const handleHeroSearch = (e) => {
    const query = e.target.value;
    setHeroSearchQuery(query);
    setPendingSearchLocation(null);
    onSearchLocationSelected?.(null);
    setHeroHighlightedIndex(-1);
    setHeroIsNavigatingWithKeyboard(false);

    if (query.length > 0) {
      const lowercaseQuery = query.toLowerCase();
      const metroLetters = ['m', 'me', 'met', 'metr', 'metro'];
      const words = lowercaseQuery.split(/\s+/);
      const hasMetroWord = words.some(word => metroLetters.includes(word));
      const queryWithoutMetro = hasMetroWord
        ? lowercaseQuery.replace(/\b(m|me|met|metr|metro)\b/g, '').trim()
        : lowercaseQuery;

      const results = [
        ...processLocationResults(locationNames.City, 'city', lowercaseQuery),
        ...processLocationResults(locationNames.County, 'county', lowercaseQuery),
        ...processLocationResults(locationNames.Metro, 'metro', hasMetroWord ? queryWithoutMetro : lowercaseQuery),
        ...processLocationResults(locationNames.State, 'state', lowercaseQuery),
      ];

      setHeroSearchResults(results);
      setShowHeroDropdown(results.length > 0);
    } else {
      setHeroSearchResults([]);
      setShowHeroDropdown(false);
    }
  };

  const handleHeroLocationSelect = (location) => {
    setShowHeroDropdown(false);
    setHeroSearchQuery(getDisplayName(location));
    setHeroSearchResults([]);
    setHeroHighlightedIndex(-1);
    setHeroIsNavigatingWithKeyboard(false);
    setPendingSearchLocation(location);
    onSearchLocationSelected?.(location);
    openAuth('log_in');
  };

  const heroOverridesDeepLink =
    Boolean(heroSearchQuery?.trim()) || pendingSearchLocation != null;
  const showDeepLinkBanner = Boolean(deepLinkLocation) && !heroOverridesDeepLink;

  return (
    <div className="landing-page">
      <header className="landing-header">
        <nav className="landing-nav">
          <button 
            className="mobile-menu-button"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
          >
            {showMobileMenu ? <XIcon size={28} /> : <Menu size={28} />}
          </button>

          <div className="landing-logo">
            <div className="landing-logo-shield">
              <Shield size={50} />
              <span className="landing-logo-text-k">K</span>
            </div>
            <span>RIME</span>
          </div>

          <div className={`nav-links ${showMobileMenu ? 'mobile-open' : ''}`}>
            <button onClick={() => window.open('https://krime.blog', '_blank')} className="nav-link">
                Blog
            </button>
            <button onClick={() => scrollToSection('features')} className="nav-link">
                Features
            </button>
            <button onClick={() => scrollToSection('use-cases')} className="nav-link">
                Use Cases
            </button>
            <button onClick={() => scrollToSection('pricing')} className="nav-link">
                Pricing
            </button>
            <button onClick={() => scrollToSection('newsletter')} className="nav-link">
                Newsletter
            </button>
            
            <div className="mobile-menu-footer">
                <div className="mobile-menu-footer-links">
                <button onClick={() => { setShowInfoModal('disclaimer'); setShowMobileMenu(false); }}>Disclaimer</button>
                <button onClick={() => { setShowInfoModal('terms'); setShowMobileMenu(false); }}>Terms of Use</button>
                <button onClick={() => { setShowInfoModal('privacy'); setShowMobileMenu(false); }}>Privacy Policy</button>
                <button onClick={() => { setShowInfoModal('contact'); setShowMobileMenu(false); }}>Contact</button>
                </div>

                <div className="mobile-menu-social">
                <a href="https://x.com/krimeapp" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
                    <FaXTwitter size={20} />
                </a>
                <a href="https://youtube.com/@krimeapp" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                    <FaYoutube size={20} />
                </a>
                <a href="https://instagram.com/krimeapp" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                    <FaInstagram size={20} />
                </a>
                <a href="https://facebook.com/krimeapp" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                    <FaFacebook size={20} />
                </a>
                <a href="https://reddit.com/r/krimeapp" target="_blank" rel="noopener noreferrer" aria-label="Reddit">
                    <FaReddit size={20} />
                </a>
                </div>

                <p className="mobile-menu-copyright">&copy; 2026 KRIME Inc. All rights reserved.</p>
            </div>
          </div>

          <div className="nav-buttons">
            <button className="btn-secondary" onClick={() => openAuth('log_in')}>
              Log In
            </button>
            <button className="btn-primary" onClick={() => openAuth('sign_up')}>
              Sign Up
            </button>
          </div>
        </nav>
      </header>

      <section className="hero-section">
        <div className="hero-background"></div>
        <div className="hero-content">
          <div className={`hero ${showDeepLinkBanner ? 'hero--with-deeplink-banner' : ''}`}>
            {showDeepLinkBanner && (() => {
              const app = parseAppQuery(deepLinkSearch || '');
              const crimeWord = getCrimeDisplayName(app.crime, app.source).toLowerCase();
              const locLabel = formatDeepLinkName(deepLinkLocation);
              return (
                <div className="hero-deeplink-notice" role="status">
                  <p className="hero-deeplink-notice-text">
                    Log In or Sign Up to view {app.year} {crimeWord} for <strong>{locLabel}</strong>.
                  </p>
                </div>
              );
            })()}

            <h1 className="hero-title">Understand the <span style={{ color: 'var(--gold)' }}>true crime</span> around you</h1>
            <p className="hero-subtitle">
              Access comprehensive crime data for over 16,000 locations across the United States. 
              Make informed decisions with real-time insights and historical trends.
            </p>

            <div className="hero-search-container">
              <div className="hero-search-inner" ref={heroSearchRef}>
                <Search className="hero-search-icon" />
                <input
                  ref={heroInputRef}
                  type="text"
                  className="hero-search-input"
                  placeholder="Enter city, county, metro or state..."
                  value={heroSearchQuery}
                  onChange={handleHeroSearch}
                  onKeyDown={handleHeroKeyDown}
                  onFocus={() => {
                    if (heroSearchQuery && heroSearchResults.length > 0) {
                      setShowHeroDropdown(true);
                    }
                  }}
                />
                {heroSearchQuery && (
                  <button
                    className="hero-clear-search"
                    onClick={() => {
                      setHeroSearchQuery('');
                      setHeroSearchResults([]);
                      setShowHeroDropdown(false);
                      setHeroHighlightedIndex(-1);
                      setHeroIsNavigatingWithKeyboard(false);
                      setPendingSearchLocation(null);
                      onSearchLocationSelected?.(null);
                      heroInputRef.current?.focus();
                    }}
                  >
                    <XIcon size={16} />
                  </button>
                )}
                {showHeroDropdown && heroSearchResults.length > 0 && (
                  <ul
                    className="hero-search-dropdown"
                    ref={heroDropdownRef}
                    data-keyboard-nav={heroIsNavigatingWithKeyboard}
                    style={{ maxHeight: `${heroDropdownMaxHeight}px` }}
                  >
                    {locationTypes.slice().reverse().map(locationType => {
                      const typeResults = heroSearchResults.filter(r => r.type === locationType.value);
                      if (typeResults.length === 0) return null;
                      return (
                        <React.Fragment key={locationType.value}>
                          <li className="hero-search-dropdown-heading">{locationType.label}</li>
                          {typeResults.map((result, index) => {
                            const globalIndex = getFlattenedHeroResults().findIndex(
                              r => r.name === result.name && r.type === result.type
                            );
                            return (
                              <li
                                key={`${result.type}-${result.name}-${index}`}
                                data-index={globalIndex}
                                className={globalIndex === heroHighlightedIndex ? 'highlighted' : ''}
                                onClick={() => handleHeroLocationSelect(result)}
                                onMouseEnter={() => {
                                  setHeroHighlightedIndex(globalIndex);
                                  setHeroIsNavigatingWithKeyboard(false);
                                }}
                              >
                                {getDisplayName(result)}
                              </li>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>

      <section id="features" className="features-section">
        <h2 className="section-title">Explore Crime Data Your Way</h2>
        <div className="features-tabs">
          <div className="feature-tab">
            <div className="feature-tab-icon">
              <FileText size={40} />
            </div>
            <div className="logo feature-logo">
              <div className="logo-shield feature-logo-shield">
                <Shield size={50} />
                <span className="logo-text-k feature-logo-text-k">K</span>
              </div>
              <span>RIME</span>
              <span>Reports</span>
            </div>
            <p>Comprehensive crime reports with detailed breakdowns of incident totals, historical trends, rate comparisons, and composite scores across different time periods and locations.</p>
          </div>

          <div className="feature-tab">
            <div className="feature-tab-icon">
              <ChartPie size={40} />
            </div>
            <div className="logo feature-logo">
              <div className="logo-shield feature-logo-shield">
                <Shield size={50} />
                <span className="logo-text-k feature-logo-text-k">K</span>
              </div>
              <span>RIME</span>
              <span>Totals</span>
            </div>
            <p>View total crime counts and averages across cities, counties, metros, states, divisions, regions, and the nation. Understand the full scope of violent and property crime.</p>
          </div>

          <div className="feature-tab">
            <div className="feature-tab-icon">
              <TrendingUpDown size={40} />
            </div>
            <div className="logo feature-logo">
              <div className="logo-shield feature-logo-shield">
                <Shield size={50} />
                <span className="logo-text-k feature-logo-text-k">K</span>
              </div>
              <span>RIME</span>
              <span>Trends</span>
            </div>
            <p>Analyze crime patterns over time. Identify increasing or decreasing trends to determine the changing nature of crime in a particular area or jurisdiction.</p>
          </div>

          <div className="feature-tab">
            <div className="feature-tab-icon">
              <ChartNoAxesGantt size={40} />
            </div>
            <div className="logo feature-logo">
              <div className="logo-shield feature-logo-shield">
                <Shield size={50} />
                <span className="logo-text-k feature-logo-text-k">K</span>
              </div>
              <span>RIME</span>
              <span>Rates</span>
            </div>
            <p>Compare crime rates per capita across different locations throughout the nation. Understand risk relative to population size for accurate comparisons.</p>
          </div>

          <div className="feature-tab">
            <div className="feature-tab-icon">
              <Award size={40} />
            </div>
            <div className="logo feature-logo">
              <div className="logo-shield feature-logo-shield">
                <Shield size={50} />
                <span className="logo-text-k feature-logo-text-k">K</span>
              </div>
              <span>RIME</span>
              <span>Scores&trade;</span>
            </div>
            <p>Composite KRIME Scores&trade; rank locations based on weighted metrics for each type of violent or property crime. Quickly identify differences between locations.</p>
          </div>
        </div>
      </section>

      <section id="use-cases" className="use-cases-section">
        <div className="section-title-with-logo">
          <span>Who Uses</span>
          <div className="title-krime-logo">
            <div className="title-logo-shield">
              <Shield size={64} />
              <span className="title-logo-k">K</span>
            </div>
            <span>RIME?</span>
          </div>
        </div>
        <div className="use-cases-expanded">
          <div className="use-case-card">
            <div className="use-case-icon">
              <Users size={40} />
            </div>
            <h3>Concerned Citizens</h3>
            <p>Stay informed about crime in your neighborhood. Set up alerts for incidents near your home and track historical trends over time.</p>
            <div className="testimonial">
              <p className="testimonial-text">"KRIME helps me stay aware of what's happening in my community. I feel more prepared and informed about the true crime."</p>
              <p className="testimonial-author">— Elsy C., Cleveland</p>
            </div>
          </div>

          <div className="use-case-card">
            <div className="use-case-icon">
              <House size={40} />
            </div>
            <h3>People Moving</h3>
            <p>Research potential neighborhoods before making one of life's biggest decisions. Compare crime rates and scores across different areas.</p>
            <div className="testimonial">
              <p className="testimonial-text">"Before moving my family, I used KRIME to compare 5 different neighborhoods. It gave me peace of mind."</p>
              <p className="testimonial-author">— Steve W., Miami</p>
            </div>
          </div>

          <div className="use-case-card">
            <div className="use-case-icon">
              <Building2 size={40} />
            </div>
            <h3>Real Estate Professionals</h3>
            <p>Provide clients with detailed crime data for any property. Stand out by offering comprehensive neighborhood specific data for large cities.</p>
            <div className="testimonial">
              <p className="testimonial-text">"My clients love that I can show them detailed crime trends. It's become a key part of my presentations."</p>
              <p className="testimonial-author">— Kyle P., Real Estate Agent</p>
            </div>
          </div>

          <div className="use-case-card">
            <div className="use-case-icon">
              <Plane size={40} />
            </div>
            <h3>Travelers</h3>
            <p>Research crime in unfamiliar cities before your trip. Know which areas to avoid and where to stay for more security.</p>
            <div className="testimonial">
              <p className="testimonial-text">"I always check KRIME before booking hotels. It helps me choose the specific areas I feel most comfortable staying in."</p>
              <p className="testimonial-author">— David H., Frequent Traveler</p>
            </div>
          </div>

          <div className="use-case-card">
            <div className="use-case-icon">
              <NotebookPen size={40} />
            </div>
            <h3>Researchers & Journalists</h3>
            <p>Access comprehensive historical crime data for analysis and reporting. Export data for academic studies or investigative journalism.</p>
            <div className="testimonial">
              <p className="testimonial-text">"KRIME's historical data is invaluable for my research on urban crime patterns and policy effectiveness."</p>
              <p className="testimonial-author">— Laura T., Criminologist</p>
            </div>
          </div>

          <div className="use-case-card">
            <div className="use-case-icon">
              <ShieldUser size={40} />
            </div>
            <h3>Law Enforcement</h3>
            <p>Analyze crime patterns across jurisdictions. Identify hotspots and allocate resources more effectively with data-driven insights.</p>
            <div className="testimonial">
              <p className="testimonial-text">"KRIME helps us identify trends and allocate patrol resources where they're needed most."</p>
              <p className="testimonial-author">— Andrew R., Police Sergeant</p>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="pricing-section">
        <h2 className="section-title">Choose Your Plan</h2>
        
        <div className="billing-toggle">
            <span className={billingPeriod === 'monthly' ? 'active' : ''}>Monthly</span>
            <button 
            className="toggle-switch"
            onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
            aria-label="Toggle billing period"
            >
            <span className={`toggle-slider ${billingPeriod === 'yearly' ? 'yearly' : ''}`}></span>
            </button>
            <span className={billingPeriod === 'yearly' ? 'active' : ''}>
            Yearly <span className="save-badge">Save up to 17%</span>
            </span>
        </div>

        <div className="pricing-grid">
            <div className="pricing-card">
            <h3>Free</h3>
            <div className="price">$0<span>/month</span></div>
            <ul className="features-list">
                <li><Check size={20} /> 5 location searches to get started</li>
                <li><Check size={20} /> Advanced analytics</li>
                <li><Check size={20} /> Access to all locations</li>
                <li><Check size={20} /> Most recent year data</li>
            </ul>
            <button className="btn-outline" onClick={() => openAuth('sign_up')}>Get Started</button>
            </div>

            <div className="pricing-card">
            <h3>Lieutenant</h3>
            <div className={`price ${billingPeriod === 'monthly' ? '' : 'yearly'}`}>
                ${billingPeriod === 'monthly' ? '9.99' : '8.33'}
                <span>/month</span>
            </div>
            {billingPeriod === 'yearly' && (
                <div className="monthly-equivalent">$99.99/year billed annually</div>
            )}
            <ul className="features-list">
                <li><Check size={20} /> <strong>50 location searches each month</strong></li>
                <li><Check size={20} /> Advanced analytics</li>
                <li><Check size={20} /> Access to all locations</li>
                <li><Check size={20} /> <strong>Historical data access</strong></li>
                <li><Check size={20} /> <strong>Saved searches</strong></li>
                <li><Check size={20} /> <strong>Email support</strong></li>
                <li><Check size={20} /> <strong>10 location alerts</strong></li>
            </ul>
            <button className="btn-outline" onClick={() => openAuth('sign_up')}>Get Started</button>
            </div>

            <div className="pricing-card featured">
            <div className="popular-badge">Most Popular</div>
            <h3>Captain</h3>
            <div className={`price ${billingPeriod === 'monthly' ? '' : 'yearly'}`}>
                ${billingPeriod === 'monthly' ? '19.99' : '16.67'} 
                <span>/month</span>
            </div>
            {billingPeriod === 'yearly' && (
                <div className="monthly-equivalent">$199.99/year billed annually</div>
            )}
            <ul className="features-list">
                <li><Check size={20} /> <strong>200 location searches each month</strong></li>
                <li><Check size={20} /> Advanced analytics</li>
                <li><Check size={20} /> Access to all locations</li>
                <li><Check size={20} /> Historical data access</li>
                <li><Check size={20} /> Saved searches</li>
                <li><Check size={20} /> Email support</li>
                <li><Check size={20} /> <strong>20 location alerts</strong></li>
                <li><Check size={20} /> <strong>Export data to Text/CSV/Excel/PDF/PNG</strong></li>
            </ul>
            <button className="btn-primary" onClick={() => openAuth('sign_up')}>Get Started</button>
            </div>

            <div className="pricing-card">
            <h3>Chief</h3>
            <div className={`price ${billingPeriod === 'monthly' ? '' : 'yearly'}`}>
                ${billingPeriod === 'monthly' ? '39.99' : '33.33'}
                <span>/month</span>
            </div>
            {billingPeriod === 'yearly' && (
                <div className="monthly-equivalent">$399.99/year billed annually</div>
            )}
            <ul className="features-list">
                <li><Check size={20} /> <strong>Unlimited location searches at any time</strong></li>
                <li><Check size={20} /> Advanced analytics</li>
                <li><Check size={20} /> Access to all locations</li>
                <li><Check size={20} /> Historical data access</li>
                <li><Check size={20} /> Saved searches</li>
                <li><Check size={20} /> Email support</li>
                <li><Check size={20} /> <strong>Unlimited alerts</strong></li>
                <li><Check size={20} /> Export data to Text/CSV/Excel/PDF/PNG</li>
                <li><Check size={20} /> <strong>Monthly Chief Council</strong></li>
            </ul>
            <button className="btn-outline" onClick={() => openAuth('sign_up')}>Get Started</button>
            </div>
        </div>
        
        <div className="enterprise-cta">
            <p>Need custom API access or enterprise features? <a href="mailto:sales@krime.app">Contact us</a> for pricing.</p>
        </div>
      </section>

      <section id="newsletter" className="newsletter-section">
        <div className="newsletter-content">
          <div className="newsletter-icon">
            <Mail size={48} />
          </div>
          <h2 className="section-title">Stay Informed</h2>
          <p className="newsletter-description">
            Get the latest crime insights, data updates, and safety tips delivered to your inbox. 
            Join our community of informed citizens making smarter decisions about where they live, work, and travel.
          </p>
          
          {newsletterStatus === 'success' ? (
            <div className="newsletter-success">
              <Check size={24} />
              <p>Thanks for subscribing! Check your email to confirm.</p>
            </div>
          ) : (
            <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
              <input
                type="email"
                placeholder="Enter your email address"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                required
                className="newsletter-input"
              />
              <button type="submit" className="btn-secondary btn-large">
                Subscribe
              </button>
            </form>
          )}
          
          <p className="newsletter-note">
            We respect your privacy. Unsubscribe at any time.
          </p>
        </div>
      </section>

      <section className="cta-section">
        <h2>Ready to understand <span style={{ color: 'var(--gold)' }}>true crime</span> in your area?</h2>
        <p>Start with 5 free searches. No credit card required.</p>
        <button className="btn-primary btn-large" onClick={() => openAuth('sign_up')}>
          Get Started
        </button>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer-content">
          <div className="landing-footer-top">
            <div className="landing-logo">
              <div className="landing-logo-shield">
                <Shield size={50} />
                <span className="landing-logo-text-k">K</span>
              </div>
              <span>RIME</span>
            </div>
            
            <div className="landing-footer-links">
              <button onClick={() => setShowInfoModal('disclaimer')}>Disclaimer</button>
              <button onClick={() => setShowInfoModal('terms')}>Terms of Use</button>
              <button onClick={() => setShowInfoModal('privacy')}>Privacy Policy</button>
              <button onClick={() => setShowInfoModal('contact')}>Contact</button>
            </div>

            <div className="social-links">
              <a href="https://x.com/krimeapp" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
                <FaXTwitter size={20} />
              </a>
              <a href="https://youtube.com/@krimeapp" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                <FaYoutube size={20} />
              </a>
              <a href="https://instagram.com/krimeapp" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <FaInstagram size={20} />
              </a>
              <a href="https://facebook.com/krimeapp" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <FaFacebook size={20} />
              </a>
              <a href="https://reddit.com/r/krimeapp" target="_blank" rel="noopener noreferrer" aria-label="Reddit">
                <FaReddit size={20} />
              </a>
            </div>
          </div>
          
          <div className="landing-footer-bottom">
            <p>&copy; 2026 KRIME Inc. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {showAuthModal && (
        <AuthModal 
          initialView={authView}
          skipBodyScrollLock
          onLoginSuccess={() => { loginJustSucceeded.current = true; }}
          onSignUpSuccess={() => { signUpJustSucceeded.current = true; }}
          onClose={() => {
            setShowAuthModal(false);
            if (!loginJustSucceeded.current && !signUpJustSucceeded.current) {
              setHeroSearchQuery('');
              setHeroSearchResults([]);
              setHeroHighlightedIndex(-1);
              setShowHeroDropdown(false);
              setPendingSearchLocation(null);
              onSearchLocationSelected?.(null);
            }
            loginJustSucceeded.current = false;
            signUpJustSucceeded.current = false;
          }}
        />
      )}

      {showInfoModal && (
        <InfoModal
          type={showInfoModal}
          onClose={() => setShowInfoModal(null)}
        />
      )}
    </div>
  );
};

export default LandingPage;